<template>
  <b-modal
    ref="modalBankAccount"
    scrollable
    no-close-on-esc
    no-enforce-focus
    hide-header-close
    no-close-on-backdrop
    :title="ModalBankAccountTitle"
  >
    <ValidationObserver
      ref="formBankAccount"
    >
      <form
        role="form"
        novalidate
        @submit.prevent
      >
        <b-row>
          <b-col
            cols="12"
          >
            <ValidationProvider
              #default="{ errors }"
              name="bank"
              vid="bank"
              rules="required"
            >
              <b-form-group
                label="Bank"
                label-for="bank_name"
                label-class="font-weight-bolder"
                class="mb-2"
              >
                <v-select
                  v-model="selected.bank"
                  input-id="bank_name"
                  type="text"
                  label="bank_name"
                  placeholder="search bank here"
                  :options="options.banks"
                  :class="{ 'is-invalid': errors.length > 0 }"
                  :disabled="state.busy || fetching.banks"
                  :loading="fetching.banks"
                >
                  <template #no-options="">
                    no available bank
                  </template>
                </v-select>
                <div
                  v-if="errors.length > 0"
                  class="invalid-feedback"
                >
                  <span>{{ errors[0] }}</span>
                </div>
              </b-form-group>
            </ValidationProvider>
          </b-col>

          <b-col
            cols="12"
          >
            <ValidationProvider
              #default="{ errors }"
              name="account number"
              vid="bank_account_number"
              rules="required|max:100"
            >
              <b-form-group
                label="Account Number"
                label-for="bank_account_number"
                label-class="font-weight-bolder"
                class="mb-2"
              >
                <b-input
                  id="bank_account_number"
                  v-model="bankAccount.bank_account_number"
                  type="text"
                  maxlength="100"
                  autocomplete="off"
                  placeholder="enter account number here"
                  :state="errors.length > 0 ? false : null"
                  :disabled="state.busy"
                  @keypress="isNumber($event)"
                />
                <div
                  v-if="errors.length > 0"
                  class="invalid-feedback"
                >
                  <span>{{ errors[0] }}</span>
                </div>
              </b-form-group>
            </ValidationProvider>
          </b-col>

          <b-col
            cols="12"
          >
            <ValidationProvider
              #default="{ errors }"
              name="account name"
              vid="bank_account_name"
              rules="required|max:100"
            >
              <b-form-group
                label="Account Name"
                label-for="bank_account_name"
                label-class="font-weight-bolder"
                class="mb-2"
              >
                <b-input
                  id="bank_account_name"
                  v-model="bankAccount.bank_account_name"
                  type="text"
                  maxlength="100"
                  autocomplete="off"
                  placeholder="enter account name"
                  :state="errors.length > 0 ? false : null"
                  :disabled="state.busy"
                />
                <div
                  v-if="errors.length > 0"
                  class="invalid-feedback"
                >
                  <span>{{ errors[0] }}</span>
                </div>
              </b-form-group>
            </ValidationProvider>
          </b-col>

          <b-col
            v-if="state.editing"
            cols="12"
          >
            <b-form-group>
              <b-form-checkbox
                id="is_active"
                v-model="bankAccount.active"
                switch
                value="1"
                name="is_active"
                unchecked-value="0"
                :disabled="state.busy"
              >
                Is Active?
              </b-form-checkbox>
            </b-form-group>
          </b-col>
        </b-row>
      </form>
    </ValidationObserver>

    <template #modal-footer="{ cancel }">
      <b-button
        variant="success"
        :disabled="state.busy"
        @click.prevent="onValidateBankAccount"
      >
        {{ state.editing ? 'Update' : 'Create' }} Bank Account
      </b-button>
      <b-button
        variant="outline-dark"
        :disabled="state.busy"
        @click.prevent="cancel()"
      >
        Discard
      </b-button>
    </template>

  </b-modal>
</template>
<script>
import QUERY from 'lodash'
import MISC from '@/mixins/misc'
import FORMATTER from '@/mixins/formatter'

import { STABankAccount, SSharedList } from '@/services'

export default {
  name: 'BankAccount',

  emits: ['created', 'updated'],

  mixins: [
    MISC,
    FORMATTER
  ],

  props: {
    businessPartner: {
      type: Object,
      default: () => ({
        id: null,
        partner_code: null,
        partner_name: null,
        tin: null
      })
    }
  },

  data () {
    return {
      state: {
        editing: false,
        busy: false
      },
      fetching: {
        banks: false
      },
      selected: {
        bank: null
      },
      options: {
        banks: []
      },
      bankAccount: {
        id: null,
        business_partner: this.businessPartner.id,
        bank: null,
        bank_account_number: null,
        bank_account_name: null,
        active: 1
      }
    }
  },

  computed: {
    ModalBankAccountTitle () {
      return this.state.editing ? 'Edit Bank Account' : 'Create Bank Account'
    }
  },

  watch: {
    'selected.bank' (bank) {
      this.bankAccount.bank = bank?.id || null
    }
  },

  methods: {

    isNumber (keyboardEvent) {
      const charCode = keyboardEvent.which ? keyboardEvent.which : keyboardEvent.keyCode
      if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode !== 46) {
        keyboardEvent.preventDefault()
      }
      if (charCode === 46) {
        keyboardEvent.preventDefault()
      }
    },

    async getBanks () {
      this.fetching.banks = true
      return SSharedList.getBanks().then(
        ({ data }) => {
          this.options.banks = data
          return data
        }
      ).catch(() => {
        this.options.banks = []
        return []
      }).finally(() => {
        this.fetching.banks = false
      })
    },

    createBankAccount () {
      this.state.editing = false

      this.selected.bank = null

      this.bankAccount = {
        id: null,
        business_partner: this.businessPartner.id,
        bank_account_name: null,
        bank_account_number: null,
        active: 1
      }

      this.getBanks().then(() => {
        this.$refs.modalBankAccount.show()
      })
    },

    editBankAccount (bankAccount) {
      this.state.editing = true

      this.bankAccount = {
        id: bankAccount.id,
        business_partner: this.businessPartner.id,
        bank_account_name: bankAccount.bank_account_name,
        bank_account_number: bankAccount.bank_account_number,
        active: bankAccount.active
      }

      this.getBanks().then(() => {
        this.selected.bank = QUERY.find(this.options.banks, {
          id: bankAccount.bank_id
        })
      })

      this.$refs.modalBankAccount.show()
    },

    async onValidateBankAccount () {
      await this.$refs.formBankAccount.validate().then(
        async validBankAccount => {
          if (!validBankAccount) {
            return this.swalInvalid()
          }

          this.swalConfirm({
            html: () => {
              if (this.state.editing) {
                return '<h6>Save your Changes on this Bank Account?</h6>'
              }
              return '<h6>Create this Bank Account?</h6>'
            },
            preConfirm: () => {
              this.state.busy = true

              const createOrUpdate = () => {
                return this.state.editing ? this.onPutBankAccount() : this.onPostBankAccount()
              }

              return createOrUpdate().catch(
                ({ message }) => {
                  this.$refs.formBankAccount.setErrors(message)
                }
              ).finally(() => {
                this.state.busy = false
              })
            }
          })
        }
      )
    },

    async onPostBankAccount () {
      return STABankAccount.post(this.bankAccount).then(
        ({ data }) => {
          this.$emit('created', data)
          this.swalSuccess(data.message).then(() => {
            this.$refs.modalBankAccount.hide()
          })
          return data
        }
      )
    },

    async onPutBankAccount () {
      return STABankAccount.put(this.bankAccount).then(
        ({ data: { bankAccount, message } }) => {
          this.$emit('updated', bankAccount)
          this.swalSuccess(message).then(() => {
            this.$refs.modalBankAccount.hide()
          })
        }
      )
    }
  }
}
</script>
